import {observer} from 'mobx-react-lite'
import React from 'react'
import tw from 'tailwind-styled-components'
import {Lang} from 'yougile-lang'
import {Time} from 'yougile-util'
import {Tracking, TrackingEvent} from '@common/tracking'
import {Button, LinkButton} from '@components/common/base/buttons'
import {Icon} from '@components/common/base/icon'
import {showConfirm} from '@components/common/base/notifications'
import {DivWithHint} from '@components/common/hint/hint'
import {FileViewer} from '@components/file-viewer/file-viewer'
import {cn} from '@containers/html-utils'
import {FileViewData} from '@extensions/message-utils'
import {screencastStore} from './screencast-store'

export const ScreencastControls = observer(() => {
  return (
    <>
      <CountDown />
      <BottomPanel />
      <Preview />
    </>
  )
})

const BottomPanel = observer(() => {
  const {state} = screencastStore
  if (!state.controlsVisible) {
    return null
  }

  return (
    <TwCnt>
      <TwRecording>
        <Icon
          size={16}
          icon="IconPlayerRecordFilled"
          color="error"
          className={cn(
            state.recording ? 'animate-pulse-appear' : 'opacity-50'
          )}
        />
        <div className="min-w-40">{state.elapsedTime}</div>
      </TwRecording>
      {!state.withSound && (
        <NoSound content={Lang.msg.screencast.noMicHint}>
          ({Lang.msg.screencast.noMic})
        </NoSound>
      )}
      {state.recording ? (
        <LinkButton icon="IconPlayerPause" onClick={screencastStore.pause}>
          {Lang.msg.screencast.pause}
        </LinkButton>
      ) : (
        <LinkButton icon="IconPlayerPlay" onClick={screencastStore.resume}>
          {Lang.msg.screencast.resume}
        </LinkButton>
      )}
      <LinkButton icon="IconPlayerStop" onClick={screencastStore.stopRecording}>
        {Lang.msg.screencast.stop}
      </LinkButton>
    </TwCnt>
  )
})

const NoSound = tw(DivWithHint)`
  text-generic-white
  cursor-help
  opacity-80
`

const TwRecording = tw.div`
  flex
  items-center
  gap-4
  text-generic-white
`

const TwCnt = tw.div`
  fixed
  bottom-0
  h-40
  bg-contrast-surface
  w-full
  z-2000
  flex
  items-center
  justify-end
  pr-16
  gap-16
  text-sm-regular
`

const CountDown = observer(() => {
  const {state} = screencastStore
  if (state.countdown < 1) {
    return null
  }
  return (
    <TwCountCnt>
      <TwCountNumber>{state.countdown}</TwCountNumber>
    </TwCountCnt>
  )
})

const TwCountCnt = tw.div`
  fixed
  h-full
  w-full
  z-2000
  flex
  items-center
  justify-center
  pointer-events-none
`

const TwCountNumber = tw.div`
  w-64
  h-64
  text-32
  text-generic-white
  bg-contrast-surface
  flex
  items-center
  justify-center
  rounded-full
`

const Preview = observer(() => {
  const {state} = screencastStore
  if (!state.previewUrl) {
    return null
  }
  const data: FileViewData = {
    contentType: 'video',
    timestamp: Time.now(),
    url: state.previewUrl,
    file: state.previewFile
  }
  const onClose = async () => {
    if (await showConfirm(Lang.msg.screencast.closeConfirm)) {
      screencastStore.cancelSend()
    }
  }
  return (
    <FileViewer
      files={[data]}
      selectedIndex={0}
      onClose={onClose}
      autoplay
      customControls={<PreviewControls />}
      onDownload={() => Tracking.send(TrackingEvent.screencastDownload)}
    />
  )
})

const PreviewControls = observer(() => {
  return (
    <div className="flex gap-32">
      <LinkButton onClick={screencastStore.cancelSend}>
        {Lang.msg.screencast.discardVideo}
      </LinkButton>
      <Button onClick={screencastStore.sendToChat}>
        {Lang.msg.screencast.sendToChat}
      </Button>
    </div>
  )
})
